/**
 * useNetworkStatus - Hook para detecção de status de rede em tempo real.
 * Responsabilidade: Monitorar online/offline e expor estado reativo.
 * 
 * Princípios: SRP — apenas estado de rede. DRY — reutilizável em qualquer componente.
 */
import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
    /** true se o navegador está online */
    isOnline: boolean;
    /** true se houve queda recente de rede (últimos 10s) — útil para contextualizar erros */
    hadRecentDisconnect: boolean;
}

const RECENT_DISCONNECT_WINDOW_MS = 10_000;

export function useNetworkStatus(): NetworkStatus {
    const [isOnline, setIsOnline] = useState(() =>
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );
    const [lastDisconnectAt, setLastDisconnectAt] = useState<number | null>(null);

    const handleOnline = useCallback(() => {
        setIsOnline(true);
    }, []);

    const handleOffline = useCallback(() => {
        setIsOnline(false);
        setLastDisconnectAt(Date.now());
    }, []);

    useEffect(() => {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [handleOnline, handleOffline]);

    const hadRecentDisconnect = lastDisconnectAt
        ? (Date.now() - lastDisconnectAt) < RECENT_DISCONNECT_WINDOW_MS
        : false;

    return { isOnline, hadRecentDisconnect };
}
